/* eslint-disable ember/no-computed-properties-in-native-classes */
import { getOwner } from '@ember/application';
import { A } from '@ember/array';
import type NativeArray from '@ember/array/-private/native-array';
import { computed, get, set } from '@ember/object';
import { alias, oneWay, readOnly, gt, or } from '@ember/object/computed';
import PromiseProxyMixin from '@ember/object/promise-proxy-mixin';
import ObjectProxy from '@ember/object/proxy';
import { service } from '@ember/service';
import { camelize } from '@ember/string';
import { isBlank, isEmpty, isPresent } from '@ember/utils';
import { hasMany, belongsTo, attr } from '@ember-data/model';
import type { AsyncBelongsTo, AsyncHasMany } from '@ember-data/model';
import type Model from '@ember-data/model';
import type StoreService from '@ember-data/store';
import { tracked } from '@glimmer/tracking';
import { apiAction } from '@mainmatter/ember-api-actions';
import type AgreementPageModel from 'garaje/models/agreement-page';
import type AnnouncementModel from 'garaje/models/announcement';
import type BadgeModel from 'garaje/models/badge';
import type CompanyModel from 'garaje/models/company';
import type ConfigModel from 'garaje/models/config';
import type ConnectLocationConfigurationModel from 'garaje/models/connect-location-configuration';
import type DeliveryAreaModel from 'garaje/models/delivery-area';
import type DeviceModel from 'garaje/models/device';
import type EmergencyNotificationConfigurationModel from 'garaje/models/emergency-notification-configuration';
import type EmployeeModel from 'garaje/models/employee';
import type EmployeeScreeningFlowModel from 'garaje/models/employee-screening-flow';
import type EntryModel from 'garaje/models/entry';
import type FloorModel from 'garaje/models/floor';
import type FlowModel from 'garaje/models/flow';
import type GdprConfigurationModel from 'garaje/models/gdpr-configuration';
import type GroupModel from 'garaje/models/group';
import type GroupInviteModel from 'garaje/models/group-invite';
import type IdScanConfiguration from 'garaje/models/id-scan-configuration';
import type InviteModel from 'garaje/models/invite';
import type LocationRoleModel from 'garaje/models/location-role';
import type LocationSubscriptionModel from 'garaje/models/location-subscription';
import type LocationsSetupGuideStep from 'garaje/models/locations-setup-guide-step';
import NotificationMessage, { type NotificationMessageAttributes } from 'garaje/models/notification-message';
import type PrinterModel from 'garaje/models/printer';
import PropagableModel from 'garaje/models/propagable';
import type RecurringGroupInvite from 'garaje/models/recurring-group-invite';
import type ReservationModel from 'garaje/models/reservation';
import type SamlModel from 'garaje/models/saml';
import type SkinnyLocationModel from 'garaje/models/skinny-location';
import type TenantModel from 'garaje/models/tenant';
import type TicketCategoryModel from 'garaje/models/ticket-category';
import type TicketConfigurationModel from 'garaje/models/ticket-configuration';
import type UserModel from 'garaje/models/user';
import UserDatum from 'garaje/models/user-datum';
import type VfdConfigurationModel from 'garaje/models/vfd-configuration';
import type VisitorGuideModel from 'garaje/models/visitor-guide';
import type VisitorSurveyConfigurationModel from 'garaje/models/visitor-survey-configuration';
import type VisitorsConfiguration from 'garaje/models/visitors-configuration';
import type VisualComplianceConfigurationModel from 'garaje/models/visual-compliance-configuration';
import type WebhookModel from 'garaje/models/webhook';
import type AmsSerializer from 'garaje/serializers/ams';
import type AjaxService from 'garaje/services/ajax';
import type CurrentAdminService from 'garaje/services/current-admin';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type MetricsService from 'garaje/services/metrics';
import type SkinnyLocationsService from 'garaje/services/skinny-locations';
import type StateService from 'garaje/services/state';
import { catchError } from 'garaje/utils/catch-error';
import { FlowType, APP, NON_ASSIGNABLE_FLOWS, TenantConnectionStatus } from 'garaje/utils/enums';
import type { LocaleKey } from 'garaje/utils/locale-options';
import type { ModelData, RecordArray } from 'garaje/utils/type-utils';
import urlBuilder from 'garaje/utils/url-builder';
import type { SinglePayload, SingleResponse } from 'jsonapi/response';
import { not } from 'macro-decorators';
import { TrackedArray } from 'tracked-built-ins';

const generateLink = function (linkFn: (id: string) => string, attribute: keyof Location) {
  return function (this: Location) {
    return this.ajax.request<Location>(linkFn.call(this, this.id), { type: 'POST' }).then((data: Location) => {
      const response = data;
      const key = <keyof Location>(<AmsSerializer>getOwner(this).lookup('serializer:ams')).keyForAttribute(attribute);
      void set(this, attribute, response[key]);
    });
  };
};

export interface KioskForceUpgradeMessage {
  body?: string;
  title?: string;
  dismissableOnGaraje?: boolean;
  dismissable?: boolean;
}

interface RawKioskForceUpgradeMessage {
  body: string;
  title: string;
  'dismissable-on-garaje': boolean;
}

interface RawWelcomeEmailMessage {
  body: string;
  subject: string;
}

export interface Field {
  name: string;
  componentName: string | undefined;
  show?: boolean;
  sensitive?: boolean;
  // Category key for the sensitive annotation/reveal when it differs from `name`
  // (e.g. Private Notes → DataCategory.PRIVATE_NOTES). Defaults to `name`.
  sensitiveKey?: string;
}

interface Logo {
  url: string;
  smallUrl: string;
  thumbUrl: string;
}

interface UserDocumentRequirement {
  identifier: string;
  'required-minimum': number;
  'user-document-template-configuration-ids': number[];
}

export interface Watchlist {
  list: string | null;
  notifyEmail: string | null;
  notifyPhoneNumber: string | null;
}

// This is the list of attributes with its humanize version that, when changed,
// can be propagable to other locations
const OVERWRITABLE_SETTINGS = {
  preRegistrationEnabled: 'pre-registration',
  preRegistrationRequiredEnabled: 'pre-registration required',
  ccReceptionistEnabled: 'invite notifications',
  preRegistrationNotes: 'invite email template',
  emailNotificationEnabled: 'host notification method: email',
  smsNotificationEnabled: 'host notification method: SMS',
  slackNotificationEnabled: 'host notification method: slack',
  callNotificationEnabled: 'host notification method: call',
  hostNotificationsEnabled: 'host notifications',
  autoSignOutAtMinutesSinceMidnight: 'auto sign-out',
  color: 'accent color',
  locale: 'language',
  logo: 'logo',
  address: 'address',
  name: 'location name',
  addressLineTwo: 'suite #',
};

const ONBOARDING_CHECKS = {
  taskName: 'loadLocationsTask',
  attributes: ['color'],
};

class Location extends PropagableModel {
  declare _pinnedDeviceContacts: UserModel[];
  declare _pinnedIdScanContacts: UserModel[];
  declare localFlows: RecordArray<FlowModel>;

  @service declare ajax: AjaxService;
  @service declare currentAdmin: CurrentAdminService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare metrics: MetricsService;
  @service declare skinnyLocations: SkinnyLocationsService;
  @service('state') declare stateService: StateService;
  @service declare store: StoreService;

  constructor(properties?: Record<string, unknown>) {
    super(properties);
    set(this, 'localFlows', this.store.peekAll('flow'));
  }

  async save(...parameters: Parameters<Model['save']>): Promise<this> {
    const result = await super.save(...parameters);
    // when we create/update a location we need to reload skinny locations so that it picks up the changes
    void this.skinnyLocations.loadAllTask.perform(true);
    return result;
  }

  @hasMany('announcement', { async: true, inverse: 'location' }) declare announcements: AsyncHasMany<AnnouncementModel>;
  @hasMany('delivery-areas', { async: true, inverse: 'location' })
  declare deliveryAreas: AsyncHasMany<DeliveryAreaModel>;
  @hasMany('device', { async: true, inverse: 'location' }) declare devices: AsyncHasMany<DeviceModel>;
  @hasMany('group', { async: true, inverse: null }) declare groups: AsyncHasMany<GroupModel>; // `inverse: null` because Group's `locations` relationship is to SkinnyLocation, not Location
  @hasMany('reservation', { async: true, inverse: 'location' }) declare reservations: AsyncHasMany<ReservationModel>;

  // TODO: highlighting changes for location-billing
  @hasMany('location-subscription', { async: true, inverse: 'location' })
  // @ts-ignore remove once location subscription is in TS
  declare locationSubscriptions: AsyncHasMany<LocationSubscriptionModel>;

  @computed('locationSubscriptions.{[],content}')
  get deliveriesSubscription(): LocationSubscriptionModel | undefined {
    return this.locationSubscriptions.content.find((sub) => sub.app === APP.DELIVERIES);
  }

  @computed('locationSubscriptions.{[],content}')
  get roomsSubscription(): LocationSubscriptionModel | undefined {
    return this.locationSubscriptions.content.find((sub) => sub.app === APP.ROOMS);
  }

  @computed('locationSubscriptions.{[],content}')
  get visitorsSubscription(): LocationSubscriptionModel | undefined {
    return this.locationSubscriptions.content.find((sub) => sub.app === APP.VISITORS);
  }

  @computed('locationSubscriptions.{@each.app,content}')
  get desksSubscription(): LocationSubscriptionModel | undefined {
    return this.locationSubscriptions.content.find((sub) => sub.app === APP.DESKS);
  }

  @computed('locationSubscriptions.{@each.app,content}')
  get workplaceSubscription(): LocationSubscriptionModel | undefined {
    return this.locationSubscriptions.content.find((sub) => sub.app === APP.WORKPLACE);
  }

  @computed('locationSubscriptions.{[],content}')
  get renewalDate(): Date | null {
    const renewalSub = this.locationSubscriptions.content.find((sub) => sub.nextBillingAt);
    return renewalSub ? renewalSub.nextBillingAt : null;
  }

  // employee screening setting
  @attr('boolean') declare employeeScreeningEnabled: boolean;

  @attr('boolean') declare employeeScheduleEnabled: boolean;

  // visitor capacity settings
  @attr('number') declare capacityLimit: number | null;
  @gt('capacityLimit', 0) capacityLimitEnabled!: boolean;
  @hasMany('user', { async: true, inverse: null }) declare capacityContacts: AsyncHasMany<UserModel>;

  // /settings/locations stuff
  @attr('string') declare name: string;
  @attr('boolean', { defaultValue: false }) declare primary: boolean;
  @attr('boolean') declare securityDeskLinkEnabled: boolean;
  @belongsTo('company', { async: true, inverse: 'locations' }) declare company: AsyncBelongsTo<CompanyModel>;
  @belongsTo('webhook', { async: true, inverse: 'location' }) declare webhook: AsyncBelongsTo<WebhookModel>;
  @belongsTo('badge', { async: true, inverse: null }) declare badge: AsyncBelongsTo<BadgeModel>;
  @hasMany('printer', { async: true, inverse: 'location' }) declare printers: AsyncHasMany<PrinterModel>;
  @computed('printers.content')
  get printer(): PrinterModel | undefined {
    // support printer attribute for backward compatibility
    return this.printers.content[0];
  }
  @belongsTo('saml', { async: true, inverse: null }) declare saml: AsyncBelongsTo<SamlModel>;
  @attr('string') declare samlLocationId: string;
  @attr('boolean', { defaultValue: false }) declare disabled: boolean;
  @attr('date', { defaultValue: null }) declare disabledToEmployeesAt: Date | null;
  @alias('disabled') visitorsDisabled!: this['disabled'];
  @not('disabled') visitorsEnabled!: boolean;
  @attr('string') declare locale: LocaleKey;
  @attr('string') declare timezone: string;
  /**
   * Location.logo
   * TODO - Is this object deprecated?
   */
  @attr('immutable') declare logo: Logo | null;
  @attr('immutable') declare logoUrl: string;
  @attr('immutable') declare slideUrl: string;
  @attr('immutable') declare hideLogo: boolean;
  @attr('string', { defaultValue: '#c50000' }) declare color: string;
  @attr('immutable', { defaultValue: '#c50000' }) declare buttonColor: string;
  @attr('immutable', { defaultValue: '#ffffff' }) declare buttonTextColor: string;
  @attr('string') declare apiKey: string;
  /**
   * watchlist
   *
   * {
   *  list: "String",
   *  notifyEmail: "String",
   *  notifyPhoneNumber: "string"
   * }
   */
  @attr('immutable', {
    defaultValue: () => ({
      list: null,
      notifyEmail: null,
      notifyPhoneNumber: null,
    }),
  })
  declare watchlist: Watchlist;
  @attr('boolean') declare visitorGuideEnabled: boolean;
  @belongsTo('visitor-guide', { async: true, inverse: 'location' })
  declare visitorGuide: AsyncBelongsTo<VisitorGuideModel>;
  @attr('boolean') declare visitorSurveyEnabled: boolean;
  @belongsTo('visitor-survey-configuration', { async: true, inverse: 'location' })
  declare visitorSurveyConfiguration: AsyncBelongsTo<VisitorSurveyConfigurationModel>;

  // Defaulting to a "dismissed/complete" state unless backend tells us otherwise
  @attr('boolean', { defaultValue: true }) declare deliveriesOnboardingComplete: boolean;
  @attr('boolean', { defaultValue: true }) declare visitorsOnboardingComplete: boolean;

  // Allowed values: 'defer_to_flow_settings', 'all_visitors' or 'only_signed_nda_visitors'
  @attr('string', { defaultValue: 'all_visitors' }) declare welcomeEmailPreference:
    | 'defer_to_flow_settings'
    | 'all_visitors'
    | 'only_signed_nda_visitors';

  // FF sign_out_improvements adds auto_sign_out_at_time as number of minutes from midnight
  @attr('number') declare autoSignOutAtMinutesSinceMidnight: number | null;

  @attr('number') declare entryRetentionPeriod: number | null;

  @attr('boolean') declare redactAllVisitorData: boolean;

  @attr('number') declare visitorReminderThresholdMinutes: number;

  @attr('boolean', { defaultValue: false }) declare autoSignOutAtMidnight: boolean;

  @attr('boolean') declare blocklistEnabled: boolean;

  @hasMany('user', { async: true, inverse: null }) declare blocklistContacts: AsyncHasMany<UserModel>;
  @hasMany('user', { async: true, inverse: null }) declare deviceContacts: AsyncHasMany<UserModel>;
  @hasMany('user', { async: true, inverse: null }) declare idScanContacts: AsyncHasMany<UserModel>;
  @hasMany('user', { async: true, inverse: null }) declare printerContacts: AsyncHasMany<UserModel> | UserModel[];
  @hasMany('user', { async: true, inverse: null }) declare multiTenancyVisitorContacts: AsyncHasMany<UserModel>;
  @hasMany('user', { async: true, inverse: null }) declare connectWalkInApprovalContacts: AsyncHasMany<UserModel>;
  @attr('boolean') declare visualComplianceEnabled: boolean;
  @belongsTo('visual-compliance-configuration', { async: true, inverse: 'location' })
  declare visualComplianceConfiguration: AsyncBelongsTo<VisualComplianceConfigurationModel>;

  @attr('boolean') declare fallbackNotificationsEnabled: boolean;
  @attr('boolean') declare multiTenancyVisitorNotificationsEnabled: boolean;
  @attr('boolean') declare deviceNotificationsEnabled: boolean;
  @attr('boolean') declare printerNotificationsEnabled: boolean;
  @attr('array', {
    defaultValue: () => [],
  })
  declare enabledLocales: LocaleKey[];
  @attr('boolean') declare multipleLanguagesEnabled: boolean;

  // Protect
  @attr('number') declare registrationEligibilityStartOffset: number;
  @attr('number') declare registrationEligibilityEndOffset: number;
  @attr('number') declare scheduleEligibilityOffset: number;
  @attr('array', {
    defaultValue: () => [],
  })
  declare userDocumentRequirements: UserDocumentRequirement[];
  @or('capacityLimitEnabled', 'employeeScreeningEnabled') isProtectSetup!: boolean;
  @hasMany('floor', { async: true, inverse: 'location' }) declare floors: AsyncHasMany<FloorModel>;
  @hasMany('ticket-category', { async: true, inverse: null })
  declare ticketCategories: AsyncHasMany<TicketCategoryModel>;
  @belongsTo('ticket-configuration', { async: true, inverse: 'location' })
  declare ticketConfiguration: AsyncBelongsTo<TicketConfigurationModel>;

  // Dashboard
  @hasMany('invite', { async: true, inverse: 'location' }) declare invites: AsyncHasMany<InviteModel>;
  @hasMany('entry', { async: true, inverse: 'location' }) declare entries: AsyncHasMany<EntryModel>;

  /**
   * Location.dashboardFields
   *
   * ```[{
   *   name: "string",
   *   componentName: "string"
   * }]```
   */
  @attr('tracked-array', {
    defaultValue: () => new TrackedArray(),
  })
  declare dashboardFields: Field[];

  /**
   * Location.inviteDashboardFields
   *
   * ```[{
   *   name: "string",
   *   componentName: "string"
   * }]```
   */
  @attr('tracked-array', {
    defaultValue: () => new TrackedArray(),
  })
  declare inviteDashboardFields: Field[];

  @hasMany('location-role', { async: true, inverse: 'location' })
  declare locationRoles: AsyncHasMany<LocationRoleModel>;
  @belongsTo('gdpr-configuration', { async: true, inverse: 'location' })
  declare gdprConfiguration: AsyncBelongsTo<GdprConfigurationModel>;
  @belongsTo('vfd-configuration', { async: true, inverse: 'location' })
  declare vfdConfiguration: AsyncBelongsTo<VfdConfigurationModel>;

  get group(): GroupModel | undefined {
    return this.groups.content[0];
  }

  @computed('company.id', 'id')
  get locationAdmin(): Promise<UserModel> {
    const ObjectPromiseProxy = ObjectProxy.extend(PromiseProxyMixin);
    const promise = this.store
      .query('location-role', {
        filter: {
          location: this.id,
          roles: 'Location Admin',
        },
      })
      .then((locationRoles) => {
        const admin = locationRoles.slice()[0];

        if (admin) {
          return admin.user;
        } else {
          return this.store
            .query('company-role', {
              filter: {
                // eslint-disable-next-line ember/no-get
                company: get(this, 'company.id'),
                roles: 'Global Admin',
              },
            })
            .then((adminRoles) => {
              const firstAdminRole = adminRoles.slice()[0];
              return firstAdminRole?.user;
            });
        }
      });

    return ObjectPromiseProxy.create({ promise });
  }

  // Host notification
  @attr('boolean', { defaultValue: false }) declare emailNotificationEnabled: boolean;
  @oneWay('emailNotificationEnabled') emailEnabled!: boolean;
  @attr('boolean', { defaultValue: false }) declare smsNotificationEnabled: boolean;
  @oneWay('smsNotificationEnabled') smsEnabled!: this['smsNotificationEnabled'];
  @attr('boolean', { defaultValue: false }) declare callNotificationEnabled: boolean;
  @attr('boolean', { defaultValue: false }) declare slackNotificationEnabled: boolean;
  @readOnly('slackNotificationEnabled') slackEnabled!: this['slackNotificationEnabled'];
  @attr('boolean', {
    defaultValue: true,
  })
  declare hostNotificationsEnabled: boolean;
  @attr('boolean', { defaultValue: false }) declare notifyReceptionistsOnHostReplyEnabled: boolean;
  @oneWay('hostNotificationsEnabled') pushEnabled!: this['hostNotificationsEnabled'];
  @oneWay('hostNotificationsEnabled') invitePushEnabled!: this['hostNotificationsEnabled'];
  @alias('hostNotificationsEnabled') hasNotificationEnabled!: this['hostNotificationsEnabled'];
  @attr('string') declare office365WebhookUrl: string;
  @attr('boolean', { defaultValue: false }) declare office365NotificationEnabled: boolean;

  // address fields
  @attr('string') declare address: string;
  @attr('string') declare addressLineOne: string;
  @attr('string') declare addressLineTwo: string;
  @attr('string') declare city: string;
  @attr('string') declare state: string;
  @attr('string') declare zip: string;
  @attr('string') declare country: string;
  @attr('number') declare latitude: number;
  @attr('number') declare longitude: number;
  @attr('string') declare googlePlaceId: string;

  // @ts-ignore custom attrs options
  @attr('immutable-array', {
    deserializeItem(item: NotificationMessageAttributes) {
      return new NotificationMessage(item);
    },
    serializeItem(item: NotificationMessage) {
      return item.serialize();
    },
    defaultValue: () => [],
  })
  declare customNotifications: NotificationMessage[];

  // @ts-ignore custom attrs options
  @attr('immutable-array', {
    deserializeItem(item: NotificationMessageAttributes) {
      return new NotificationMessage(item);
    },
    serializeItem(item: NotificationMessage) {
      return item.serialize();
    },
    defaultValue: () => [],
  })
  declare defaultNotifications: NotificationMessage[];

  @attr('date') declare lastEntryAt: Date;
  @attr('date') declare createdAt: Date;

  // Pre-registration
  @attr('boolean', { defaultValue: true }) declare preRegistrationEnabled: boolean;
  @attr('boolean', { defaultValue: false }) declare ccReceptionistEnabled: boolean;
  @attr('boolean', { defaultValue: false }) declare preRegistrationRequiredEnabled: boolean;
  @attr('boolean', { defaultValue: false }) declare nearVisitScreeningEnabled: boolean;
  @attr('boolean', { defaultValue: false }) declare hostApprovalEnabled: boolean;

  @attr('boolean') declare touchlessSigninEnabled: boolean;
  @attr('boolean') declare staticQrEnabled: boolean;

  invitesTemplateUrl(): string {
    const path = 'csv-template';
    const adapter = this.store.adapterFor('location');
    const url = adapter.buildURL('location', this.id);

    return `${url}/${path}`;
  }

  @attr('string') declare preRegistrationLink: string;
  @attr('string') declare securityDeskLink: string;

  @attr('string') declare preRegistrationNotes: string;

  @hasMany('flow', { async: true, inverse: 'location' }) declare flows: AsyncHasMany<FlowModel>;

  @computed('flows.[]')
  get hasNonProtectFlows(): Promise<boolean> {
    return this.flows.then((flows) => {
      return <number>flows.length > 1 && flows.any((f) => !f.isProtect);
    });
  }
  // /settings/forms stuff
  @belongsTo('config', { async: true, inverse: 'location' }) declare config: AsyncBelongsTo<ConfigModel>;

  @hasMany('locations-setup-guide-step', { async: true, inverse: 'location' })
  declare locationsSetupGuideSteps: AsyncHasMany<LocationsSetupGuideStep>;

  // ipad version
  @attr('string') declare currentVersion: string;

  // Sorts primary locations above non-primary locations
  @computed('primary')
  get sortablePrimary(): number {
    return this.primary ? 0 : 1;
  }

  // eslint-disable-next-line @typescript-eslint/unbound-method
  generateApiKey = generateLink(urlBuilder.v2.generateApiKeyUrl, 'apiKey');
  // eslint-disable-next-line @typescript-eslint/unbound-method
  generatePreRegistrationLink = generateLink(urlBuilder.v1.generatePreRegistrationLinkUrl, 'preRegistrationLink');
  // eslint-disable-next-line @typescript-eslint/unbound-method
  generateSecurityDeskLink = generateLink(urlBuilder.v2.generateSecurityDeskLinkUrl, 'securityDeskLink');

  // copy settings from existing location
  @attr('string') declare masterLocationId: string;

  // most common email domain
  @attr('string') declare domain: string;

  @attr('string') declare companyNameOverride: string | null;

  // TODO: @heroiceric
  // This is problematic because company is an async relationship
  @computed('company.name', 'companyNameOverride', 'primary')
  get companyName(): string | undefined | null {
    if (isBlank(this.companyNameOverride)) {
      // eslint-disable-next-line ember/use-ember-get-and-set
      return this.company.get('name');
    } else {
      return this.companyNameOverride;
    }
  }

  set companyName(v: string) {
    let newName: string | null = v;
    if (typeof v === 'string') {
      newName = newName.trim();
    }

    if (isBlank(newName)) {
      newName = null;
    }

    set(this, 'companyNameOverride', newName);
  }

  @computed('flows.{@each.agreementPage,content}')
  get agreementPages(): NativeArray<AsyncBelongsTo<AgreementPageModel>> {
    return A(this.flows.content.map((flow) => flow.agreementPage)).compact();
  }

  @computed('agreementPages.@each.enabled')
  get hasAgreementPage(): AsyncBelongsTo<AgreementPageModel> | undefined {
    // @ts-ignore this is problematic because agreementPages is an async relationship
    return this.agreementPages.findBy('enabled', true);
  }

  @computed('companyName', 'company.name', 'name')
  get nameWithCompanyName(): string {
    const name = this.name;
    let companyName = this.companyName;

    if (isBlank(companyName)) {
      // eslint-disable-next-line ember/use-ember-get-and-set
      companyName = this.company.get('name');
    }

    return isBlank(companyName) ? name : `${companyName} ${name}`;
  }

  @computed('multipleLanguagesEnabled', 'enabledLocales.[]')
  get languagesAvailable(): boolean {
    const multipleLanguagesEnabled = this.multipleLanguagesEnabled;
    const enabledLocales = this.enabledLocales || [];
    return multipleLanguagesEnabled && enabledLocales.length > 0;
  }

  @attr() declare employeesCsvUploadStatus: string;

  hasDirtyFlows(): boolean {
    return this.flows.content.some((flow) => !!flow.hasDirtyAttributes);
  }

  @computed('hasDirtyAttributes')
  get isOnlyEmployeesCSVUploadStatusDirty(): boolean {
    if (!this.hasDirtyAttributes) {
      return false;
    }
    const changedAttributes = this.changedAttributes();
    return !!(Object.keys(changedAttributes).length === 1 && this.changedAttributes()['employeesCsvUploadStatus']);
  }

  @computed('id', 'location.id')
  get skinnyLocation(): Promise<SkinnyLocationModel> | SkinnyLocationModel {
    const skinnyLocation = this.store.peekRecord('skinny-location', this.id);
    if (skinnyLocation) {
      return skinnyLocation;
    }
    return this.store.findRecord('skinny-location', this.id);
  }

  rollbackAttributes(): void {
    const cacheCsv = this.employeesCsvUploadStatus;
    super.rollbackAttributes();
    if (cacheCsv) {
      set(this, 'employeesCsvUploadStatus', cacheCsv);
    }
  }

  matchupInviteOrEntryFieldsWithLocationConfig<T extends EntryModel | InviteModel>(model: T): Promise<T> {
    return this.stateService.loadFlows({ reload: false }).then((loadedFlows) => {
      const flows = loadedFlows.filter(
        ({ employeeCentric, type }) => !NON_ASSIGNABLE_FLOWS.includes(type) && !employeeCentric,
      );
      let pov = 'Purpose of visit';

      if ('povKey' in model && model.povKey) {
        pov = model.povKey;
      }

      const flowField = model.userData.find((field) => field.field === pov);
      // We only want to extend new invites/entries and leave the
      // data untouch for existing ones.
      if (!flowField && !model.id && flows.length > 0) {
        const selectedFlow = flows[0]!;

        const { userData } = model;
        userData.addObject(new UserDatum({ field: pov, value: selectedFlow.name }));
        // need to reassign to key to detect changes
        model.userData = userData;

        model.flowName = selectedFlow.name;
        // @ts-expect-error
        model.flow = selectedFlow;
      }

      return model;
    });
  }

  createInvite({
    expectedArrivalTime,
    email,
    fullName,
    employee,
    privateNotes,
    propertyNotes,
    additionalGuests,
    additionalHosts,
  }: {
    expectedArrivalTime: Date | string;
    fullName: string;
    email: string;
    employee?: EmployeeModel | null;
    privateNotes?: string;
    propertyNotes?: string;
    additionalGuests?: number;
    additionalHosts?: EmployeeModel[];
  }): Promise<InviteModel> | InviteModel {
    const invite = this.store.createRecord('invite', {
      location: this,
      expectedArrivalTime,
      fullName,
      email,

      // Fix for SQ-5193 (no sign-in field data persisted)
      // If userData not explicitly set here, the snapshot logic in the Ember Data store
      // may pass an empty array for the userData attribute to the serializer.
      userData: [],
    });
    this.metrics.trackEvent('Creating invite for location', {
      // @ts-expect-error - not sure if okay to pass in whole models to tracking.
      invite,
      // @ts-expect-error - not sure if okay to pass in whole models to tracking.
      location: this,
    });
    employee = employee ?? this.currentAdmin.employee;

    if (employee) {
      invite.employee = <AsyncBelongsTo<EmployeeModel>>(<unknown>employee);
      invite.inviterName = employee.name;
    }

    if (privateNotes) invite.privateNotes = privateNotes;
    if (propertyNotes) invite.propertyNotes = propertyNotes;
    if (additionalHosts) invite.additionalHosts = additionalHosts;
    if (additionalGuests) invite.additionalGuests = additionalGuests;
    return this.featureFlags.isEnabled('visitors-no-default-visitor-type')
      ? invite
      : this.matchupInviteOrEntryFieldsWithLocationConfig(invite);
  }

  createEmployeeInvite(date: Date, name: string, email: string): Promise<InviteModel> {
    const invite = this.store.createRecord('invite', {
      location: this,
      expectedArrivalTime: date,
      fullName: name,
      email: email,
    });

    this.metrics.trackEvent('Creating invite for location', {
      // @ts-expect-error - not sure if okay to pass in whole models to tracking.
      invite,
      // @ts-expect-error - not sure if okay to pass in whole models to tracking.
      location: this,
    });

    const employee = this.currentAdmin.employee;

    if (employee) {
      invite.employee = <AsyncBelongsTo<EmployeeModel>>(<unknown>employee);
      invite.inviterName = employee.name;
    }
    return this.matchupInviteOrEntryFieldsWithLocationConfig(invite);
  }

  createEntry(date: Date): Promise<EntryModel> {
    const signInUser = this.currentAdmin.user;
    const entry = this.store.createRecord('entry', {
      signedInAt: date,
      location: this,
      signInUser,
      sendHostNotification: true,
    });

    return this.matchupInviteOrEntryFieldsWithLocationConfig(entry);
  }

  createGroupInvite({
    expectedArrivalTime,
    recurringRule,
  }: {
    expectedArrivalTime: Date | string;
    recurringRule?: string;
  }): GroupInviteModel | RecurringGroupInvite {
    const { employee = null } = this.currentAdmin;
    const model = recurringRule ? 'recurring-group-invite' : 'group-invite';

    const groupInvite = this.store.createRecord(model, {
      location: this,
      expectedArrivalTime: recurringRule ? null : expectedArrivalTime,
      startTime: recurringRule ? expectedArrivalTime : null,
      employee,
      recurringRule,
    });

    this.metrics.trackEvent('Creating group invite for location', {
      // @ts-expect-error - not sure if okay to pass in whole models to tracking.
      groupInvite,
      // @ts-expect-error - not sure if okay to pass in whole models to tracking.
      location: this,
    });

    return groupInvite;
  }

  findEntry(id: string, include: string[] = [], fields: Record<string, string> = {}): Promise<EntryModel> {
    return this.store
      .findRecord('entry', id, { reload: true, include: include.join(), adapterOptions: { fields } })
      .then((entry) => {
        return this.matchupInviteOrEntryFieldsWithLocationConfig(entry);
      });
  }

  // use: employeeScreeningFlow: alias('skinnyLocation.employeeScreeningFlow'),
  @computed('localFlows.@each.type', 'skinnyLocation.employeeScreeningFlow.id')
  get employeeScreeningFlow(): EmployeeScreeningFlowModel | FlowModel | null | undefined {
    // this is async. Need to cast to EmployeeScreeningFlowModel
    const flow = <
      AsyncBelongsTo<EmployeeScreeningFlowModel> | undefined // eslint-disable-next-line ember/no-get
    >get(this.skinnyLocation, 'employeeScreeningFlow');
    if (isPresent(flow) && flow.content?.id) {
      return flow.content;
    }
    if (isEmpty(this.localFlows)) {
      return null;
    }
    return this.localFlows.findBy('type', FlowType.EMPLOYEE_SCREENING);
  }

  // use: hasEmployeeScreeningFlow: alias('employeeScreeningFlow.id'),
  @computed('employeeScreeningFlow.locations.[]', 'id', 'skinnyLocation.employeeScreeningFlow.id')
  get hasEmployeeScreeningFlow(): boolean {
    // eslint-disable-next-line ember/no-get
    if (isPresent(get(this, 'skinnyLocation.employeeScreeningFlow.id'))) {
      return true;
    }
    if (!this.employeeScreeningFlow) {
      return false;
    }
    const locationIds = this.employeeScreeningFlow.hasMany('locations').ids();
    return locationIds.includes(this.id);
  }

  firstFlow(): Promise<FlowModel | undefined> {
    /*
     API V2 is not consistently returning the flows relationship, we
     can't rely on get(this, 'flows')
     */
    return this.store.query('flow', { filter: { location: this.id } }).then(function (flows) {
      // Companies will have 1 flow by default.
      return flows.slice().toSorted((a, b) => a.position - b.position)[0];
    });
  }

  async kioskForceUpgradeMessage(): Promise<SingleResponse<RawKioskForceUpgradeMessage>> {
    return await apiAction<SingleResponse<RawKioskForceUpgradeMessage>>(this, {
      method: 'GET',
      path: 'kiosk-force-upgrade-message',
      requestType: 'v3',
    });
  }

  async getWelcomeEmailPreview(): Promise<SingleResponse<RawWelcomeEmailMessage>> {
    return await apiAction(this, {
      method: 'GET',
      path: 'welcome-email-preview',
      requestType: 'v3',
    });
  }

  @tracked _visitorsConfiguration?: VisitorsConfiguration;

  get visitorsConfiguration(): VisitorsConfiguration | undefined {
    return this._visitorsConfiguration;
  }

  async loadVisitorsConfiguration(): Promise<VisitorsConfiguration | undefined> {
    if (this._visitorsConfiguration) return this._visitorsConfiguration;

    const results = await catchError(
      this.store.query('visitors-configuration', {
        filter: { location_id: this.id },
      }),
    );
    this._visitorsConfiguration = results?.slice()[0];
    return this._visitorsConfiguration;
  }

  @tracked _idScanConfiguration?: IdScanConfiguration;

  get idScanConfiguration(): IdScanConfiguration | undefined {
    return this._idScanConfiguration;
  }

  async loadIdScanConfiguration(): Promise<IdScanConfiguration | undefined> {
    if (this._idScanConfiguration) return this._idScanConfiguration;

    const results = await catchError(
      this.store.query('id-scan-configuration', {
        filter: { location_id: this.id },
      }),
    );
    this._idScanConfiguration = results?.slice()[0];
    return this._idScanConfiguration;
  }

  async getEmergencyNotificationConfiguration(payload?: object): Promise<EmergencyNotificationConfigurationModel> {
    const loadedConfigurations = this.store.peekAll('emergency-notification-configuration');
    const matchingConfiguration = loadedConfigurations.find((c) => c.belongsTo('location').id() === this.id);

    if (matchingConfiguration) return matchingConfiguration;

    const response = await apiAction<SinglePayload<EmergencyNotificationConfigurationModel>>(this, {
      method: 'GET',
      path: 'emergency-notification-configuration',
      data: {
        include: 'location',
        fields: {
          location: 'id',
        },
        ...payload,
      },
      requestType: 'v3',
    });

    if (!response.data) {
      return this.store.createRecord('emergency-notification-configuration');
    }

    const serializer = this.store.serializerFor('emergency-notification-configuration');
    const EmergencyNotificationConfiguration = this.store.modelFor('emergency-notification-configuration');
    const normalized = serializer.normalizeSingleResponse(
      this.store,
      // @ts-expect-error
      EmergencyNotificationConfiguration,
      response,
      response.data.id,
      'findRecord',
    );

    return <EmergencyNotificationConfigurationModel>this.store.push(normalized);
  }

  hasDirtyWelcomeEmailPreference(): boolean {
    const changedAttrs = this.changedAttributes();
    return Object.keys(changedAttrs).includes('welcomeEmailPreference');
  }

  async pinIdScanContacts(): Promise<void> {
    const idScanContacts = await this.idScanContacts;
    set(this, '_pinnedIdScanContacts', idScanContacts.slice());
  }

  rollbackIdScanContacts(): void {
    // @ts-expect-error
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    void this.idScanContacts.content.setObjects(A(this._pinnedIdScanContacts));
  }

  async updateDeviceContacts(arrayOfUsers: UserModel[]): Promise<void> {
    const deviceContacts = await this.deviceContacts;
    deviceContacts.setObjects(A(arrayOfUsers));
  }

  async pinDeviceContacts(): Promise<void> {
    const deviceContacts = await this.deviceContacts;
    set(this, '_pinnedDeviceContacts', deviceContacts.slice());
  }

  rollbackDeviceContacts(): void {
    // @ts-expect-error
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    void this.deviceContacts.content.setObjects(A(this._pinnedDeviceContacts));
  }

  getPropertyConnections(): Promise<RecordArray<TenantModel>> {
    return this.store.query('tenant', {
      filter: {
        location: this.id,
        status: TenantConnectionStatus.CONNECTED,
      },
    });
  }

  async getConnectConfiguration(): Promise<ConnectLocationConfigurationModel | undefined> {
    return (
      await this.store.query('connect-location-configuration', {
        filter: {
          location: this.id,
        },
      })
    ).slice()[0];
  }

  async isConnectedToProperty(): Promise<boolean> {
    const tenants = await this.getPropertyConnections();
    return <number>tenants.length > 0;
  }

  async getVfdConfiguration(): Promise<VfdConfigurationModel | undefined> {
    let skinnyLocation = this.store.peekRecord('skinny-location', this.id);
    if (!skinnyLocation) {
      await this.skinnyLocations.loadAllTask.perform();
      skinnyLocation = this.store.peekRecord('skinny-location', this.id);
    }
    return skinnyLocation?.vfdConfiguration;
  }

  toJSON(): ModelData<Location> {
    const {
      data: { attributes },
    } = <SinglePayload<Location>>this.serialize();

    return Object.entries(attributes).reduce((carry: Record<string, unknown>, [key, value]) => {
      carry[camelize(key)] = value;

      return carry;
    }, {}) as ModelData<Location>;
  }

  static OVERWRITABLE_SETTINGS = OVERWRITABLE_SETTINGS;
  static ONBOARDING_CHECKS = ONBOARDING_CHECKS;
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    location: Location;
  }
}

export default Location;
